/**
 * SnoozeFollowupActions — Snooze + Follow-up reminder controls for the
 * selected-email detail header.
 *
 * Iteration 106 — Email Inbox Revamp Batch 4 (i + j).
 *
 * Renders two dropdown buttons:
 *   • Snooze (presets: Later today / Tomorrow morning / Next Monday / 1 week / Custom)
 *   • Follow-up reminder (presets: 3 days / 1 week / 2 weeks / Custom)
 *
 * Calls /api/email/snooze, /api/email/unsnooze, /api/email/follow-up,
 * /api/email/clear-follow-up. Calls onChange() after any successful mutation
 * so the parent can refresh state.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Clock, BellRing, X } from 'lucide-react';

function nextMondayMorning() {
  const d = new Date();
  const dow = d.getDay();
  const add = (8 - dow) % 7 || 7;
  d.setDate(d.getDate() + add);
  d.setHours(9, 0, 0, 0);
  return d;
}

const SNOOZE_PRESETS = [
  { id: 'later-today',   label: 'Later today (3h)', when: () => new Date(Date.now() + 3 * 60 * 60 * 1000) },
  { id: 'tomorrow-9am',  label: 'Tomorrow 9 AM',    when: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
  { id: 'next-monday',   label: 'Next Monday 9 AM', when: nextMondayMorning },
  { id: 'one-week',      label: 'In 1 week',        when: () => new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) },
];

const FOLLOWUP_PRESETS = [
  { id: '3-days',  label: 'In 3 days',  days: 3 },
  { id: '1-week',  label: 'In 1 week',  days: 7 },
  { id: '2-weeks', label: 'In 2 weeks', days: 14 },
];

function fmt(d) {
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function SnoozeFollowupActions({ email, token, onChange }) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [followOpen, setFollowOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setSnoozeOpen(false);
        setFollowOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const isSnoozed = !!email.snoozed_until && new Date(email.snoozed_until) > new Date();
  const followUpAt = email.follow_up_at ? new Date(email.follow_up_at) : null;
  const followUpActive = followUpAt && !isNaN(followUpAt.getTime());

  const callApi = async (path, body) => {
    setBusy(true);
    setErr('');
    try {
      await axios.post(`${API}/email/${path}`, body, { headers: { Authorization: `Bearer ${token}` } });
      setSnoozeOpen(false);
      setFollowOpen(false);
      if (onChange) onChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Action failed');
      setTimeout(() => setErr(''), 3000);
    } finally {
      setBusy(false);
    }
  };

  const snoozeUntil = (date) => callApi('snooze', { email_id: email.id, until: date.toISOString() });
  const unsnooze    = ()     => callApi('unsnooze', { email_id: email.id });
  const setFollowUp = (days) => callApi('follow-up', { email_id: email.id, after_days: days });
  const clearFollow = ()     => callApi('clear-follow-up', { email_id: email.id });

  const customSnooze = () => {
    const raw = window.prompt('Snooze until (YYYY-MM-DD HH:MM):');
    if (!raw) return;
    const d = new Date(raw);
    if (isNaN(d.getTime()) || d <= new Date()) {
      setErr('Invalid date or in the past');
      setTimeout(() => setErr(''), 3000);
      return;
    }
    snoozeUntil(d);
  };
  const customFollow = () => {
    const raw = window.prompt('Follow up in how many days?', '5');
    const n = parseInt(raw, 10);
    if (!n || n <= 0) return;
    setFollowUp(n);
  };

  return (
    <div className="inline-flex items-center gap-1 relative" ref={wrapRef} data-testid="snooze-followup-actions">
      {/* ── Snooze button ──────────────────────────────────────────────── */}
      <div className="relative">
        {isSnoozed ? (
          <button
            onClick={unsnooze}
            disabled={busy}
            className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1.5 inline-flex items-center gap-1 text-blue-600 dark:text-blue-400"
            title={`Snoozed until ${fmt(new Date(email.snoozed_until))} — click to unsnooze`}
            data-testid="unsnooze-btn"
          >
            <Clock className="w-4 h-4" />
            Snoozed
            <X className="w-3 h-3" />
          </button>
        ) : (
          <button
            onClick={() => { setSnoozeOpen((v) => !v); setFollowOpen(false); }}
            disabled={busy}
            className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1.5 inline-flex items-center gap-1"
            data-testid="snooze-btn"
            title="Snooze this email"
          >
            <Clock className="w-4 h-4" />
            Snooze
          </button>
        )}
        {snoozeOpen && !isSnoozed && (
          <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-30 py-1" data-testid="snooze-menu">
            {SNOOZE_PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => snoozeUntil(p.when())}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-between"
                data-testid={`snooze-${p.id}`}
              >
                <span>{p.label}</span>
                <span className="text-xs text-slate-400">{fmt(p.when())}</span>
              </button>
            ))}
            <button
              onClick={customSnooze}
              className="w-full text-left px-3 py-1.5 text-sm text-blue-600 hover:bg-slate-50 dark:hover:bg-slate-700"
              data-testid="snooze-custom"
            >Custom…</button>
          </div>
        )}
      </div>

      {/* ── Follow-up button ──────────────────────────────────────────── */}
      <div className="relative">
        {followUpActive ? (
          <button
            onClick={clearFollow}
            disabled={busy}
            className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1.5 inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"
            title={`Follow-up reminder set for ${fmt(followUpAt)} — click to clear`}
            data-testid="clear-followup-btn"
          >
            <BellRing className="w-4 h-4" />
            Reminding {fmt(followUpAt)}
            <X className="w-3 h-3" />
          </button>
        ) : (
          <button
            onClick={() => { setFollowOpen((v) => !v); setSnoozeOpen(false); }}
            disabled={busy}
            className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1.5 inline-flex items-center gap-1"
            data-testid="followup-btn"
            title="Set a follow-up reminder"
          >
            <BellRing className="w-4 h-4" />
            Follow up
          </button>
        )}
        {followOpen && !followUpActive && (
          <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-30 py-1" data-testid="followup-menu">
            {FOLLOWUP_PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => setFollowUp(p.days)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                data-testid={`followup-${p.id}`}
              >{p.label}</button>
            ))}
            <button
              onClick={customFollow}
              className="w-full text-left px-3 py-1.5 text-sm text-blue-600 hover:bg-slate-50 dark:hover:bg-slate-700"
              data-testid="followup-custom"
            >Custom…</button>
          </div>
        )}
      </div>

      {err && <span className="text-xs text-rose-600 ml-2" data-testid="snooze-followup-error">{err}</span>}
    </div>
  );
}
